import { useEffect, useRef, useCallback, MutableRefObject, Dispatch, SetStateAction } from "react";
import L from "leaflet";
import { isValidCoordinates } from "../utils/isValidCoordinates";
import { safeCoordTuple } from "../utils/safeCoordTuple";
import { geocodeAddress } from "../utils/geocodeAddress";
import { TargetLocation } from "../dtos/TargetLocation";
import { CommonMessage } from "../../../base/components/message/CommonMessage";
import { FormInstance } from "../../../base/components/form/Common";

interface UseTargetMarkerProps {
  form: FormInstance;
  addressField: string;
  coordLatField?: string;
  coordLngField?: string;
  stepLabel: string;
  stepIcon: string;
  mapInst: MutableRefObject<L.Map | null>;
  mapReady: boolean;
  isMountedRef: MutableRefObject<boolean>;
  targetLocation: TargetLocation | null;
  setTargetLocation: Dispatch<SetStateAction<TargetLocation | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  accentColor: string;
  radiusKm: number;
  skipMarker?: boolean;
}

interface UseTargetMarkerResult {
  targetMarkerRef: MutableRefObject<L.Marker | null>;
  circleRef: MutableRefObject<L.Circle | null>;
  refCircleRef: MutableRefObject<L.Circle | null>;
  processGeocoding: () => Promise<void>;
}

export const useTargetMarker = ({
  form,
  addressField,
  coordLatField,
  coordLngField,
  stepLabel,
  stepIcon,
  mapInst,
  mapReady,
  isMountedRef,
  targetLocation,
  setTargetLocation,
  setLoading,
  accentColor,
  radiusKm,
  skipMarker = false,
}: UseTargetMarkerProps): UseTargetMarkerResult => {
  const targetMarkerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const refCircleRef = useRef<L.Circle | null>(null);

  const processGeocoding = useCallback(async () => {
    const address: string | undefined = form.getFieldValue(addressField);
    if (!address?.trim()) return;

    const cachedLat = coordLatField ? form.getFieldValue(coordLatField) : null;
    const cachedLng = coordLngField ? form.getFieldValue(coordLngField) : null;

    if (cachedLat != null && cachedLng != null && isValidCoordinates(Number(cachedLat), Number(cachedLng))) {
      const lat = Number(cachedLat);
      const lng = Number(cachedLng);
      setTargetLocation({ lat, lng, label: address });
      if (mapInst.current && mapReady) {
        const tuple = safeCoordTuple(lat, lng);
        if (tuple) {
          try {
            mapInst.current.flyTo(tuple, 14, { duration: 1.5 });
          } catch {}
        }
      }
      return;
    }

    setLoading(true);
    try {
      const result = await geocodeAddress(address);
      if (!isMountedRef.current) return;
      if (result && isValidCoordinates(result.lat, result.lng)) {
        setTargetLocation({ lat: result.lat, lng: result.lng, label: address });
        if (coordLatField) form.setFieldValue(coordLatField, result.lat);
        if (coordLngField) form.setFieldValue(coordLngField, result.lng);
        if (mapInst.current && mapReady) {
          const tuple = safeCoordTuple(result.lat, result.lng);
          if (tuple) {
            mapInst.current.flyTo(tuple, 14, { duration: 1.5 });
          }
        }
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [form, addressField, coordLatField, coordLngField, mapInst, mapReady, isMountedRef, setTargetLocation, setLoading]);

  useEffect(() => {
    if (!isMountedRef.current || !mapInst.current || !mapReady || !targetLocation) return;
    if (!isValidCoordinates(targetLocation.lat, targetLocation.lng)) return;

    if (targetMarkerRef.current) {
      mapInst.current.removeLayer(targetMarkerRef.current);
      targetMarkerRef.current = null;
    }
    if (circleRef.current) {
      mapInst.current.removeLayer(circleRef.current);
      circleRef.current = null;
    }
    if (refCircleRef.current) {
      mapInst.current.removeLayer(refCircleRef.current);
      refCircleRef.current = null;
    }

    const tuple = safeCoordTuple(targetLocation.lat, targetLocation.lng);
    if (!tuple || !mapInst.current) return;

    try {
      circleRef.current = L.circle(tuple, {
        radius: radiusKm * 1000,
        color: accentColor,
        fillColor: accentColor,
        fillOpacity: 0.07,
        weight: 2,
        dashArray: "5, 5",
      }).addTo(mapInst.current);
    } catch (err) {
      CommonMessage.error("geofence circle error:");
    }

    try {
      refCircleRef.current = L.circle(tuple, {
        radius: radiusKm * 1000,
        color: accentColor,
        fillOpacity: 0,
        weight: 1,
        dashArray: "3, 3",
        opacity: 0.35,
      }).addTo(mapInst.current);
    } catch (err) {
      CommonMessage.error("ref circle error:");
    }
  }, [targetLocation, accentColor, mapReady, mapInst, isMountedRef, radiusKm, stepLabel, stepIcon, skipMarker]);

  return { targetMarkerRef, circleRef, refCircleRef, processGeocoding };
};
